/**
 * VistaCocinaSegmentada — sección que muestra recetas + insumos por
 * segmento DÍA / NOCHE / TRANSVERSAL en la pantalla de Cocina.
 *
 * Solo aparece cuando hay datos segmentados (algún pedido del día
 * pasó por una operación con flag_pedido_segmentado=1). Cuando todos
 * los pedidos del día son legacy (segmento NULL) este componente NO
 * se renderiza y Cocina queda como hoy.
 *
 * Insumos (r.es_insumo=1) van en una sub-sección "Para despacho — no
 * requiere cocción" debajo de las recetas de cada tab.
 */

import React, { useState } from 'react';
import { Sun, Moon, Boxes, Package } from 'lucide-react';
import type { VistaPorSegmento } from '../../services/cocinaService';

type Segmento = 'dia' | 'noche' | 'transversal';

interface Props {
  vista: VistaPorSegmento;
}

const SEG_META: Record<
  Segmento,
  { label: string; icon: React.ReactNode; chipBg: string; ring: string }
> = {
  dia: {
    label: 'Día',
    icon: <Sun className="w-4 h-4" />,
    chipBg: 'bg-amber-500 text-white',
    ring: 'ring-amber-300',
  },
  noche: {
    label: 'Noche',
    icon: <Moon className="w-4 h-4" />,
    chipBg: 'bg-indigo-600 text-white',
    ring: 'ring-indigo-300',
  },
  transversal: {
    label: 'Transversal',
    icon: <Boxes className="w-4 h-4" />,
    chipBg: 'bg-gray-700 text-white',
    ring: 'ring-gray-300',
  },
};

const VistaCocinaSegmentada: React.FC<Props> = ({ vista }) => {
  const [segActivo, setSegActivo] = useState<Segmento>('dia');

  const segmentos: Segmento[] = ['dia', 'noche', 'transversal'];
  const datos = vista[segActivo];

  const renderItems = (
    items: typeof datos.recetas,
    titulo: string,
    chipText: string,
    Icon: React.FC<{ className?: string }>
  ) => (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-gray-500" />
        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
          {titulo}
        </h4>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          ({items.length})
        </span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-gray-400 italic">Sin items en {chipText}.</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {items.map((it) => (
            <li
              key={`${it.recetaId}-${segActivo}`}
              className="flex items-center justify-between py-2"
              data-testid={`seg-${segActivo}-item-${it.recetaId}`}
            >
              <div className="min-w-0">
                <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                  {it.recetaNombre}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {it.categoria} · {it.pedidoIds.length} pedido(s) · estado:{' '}
                  {it.estado}
                </p>
              </div>
              <span className="tabular-nums font-semibold text-gray-900 dark:text-gray-100 ml-3">
                {it.cantidadTotal}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <section
      className="mb-6 rounded-2xl bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700 shadow-sm p-4 sm:p-6"
      data-testid="vista-cocina-segmentada"
    >
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
            Vista por segmento
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Recetas a cocinar e insumos para despacho, separados por turno.
          </p>
        </div>
        <div
          role="tablist"
          aria-label="Segmento"
          className="inline-flex bg-gray-100 dark:bg-gray-700 rounded-xl p-1"
        >
          {segmentos.map((s) => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={segActivo === s}
              data-testid={`cocina-tab-${s}`}
              onClick={() => setSegActivo(s)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                segActivo === s
                  ? `${SEG_META[s].chipBg} shadow`
                  : 'text-gray-600 dark:text-gray-200 hover:bg-white/60 dark:hover:bg-gray-600'
              }`}
            >
              {SEG_META[s].icon}
              {SEG_META[s].label}
              <span className="text-[10px] tabular-nums opacity-80">
                {vista[s].recetas.length + vista[s].insumos.length}
              </span>
            </button>
          ))}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {renderItems(datos.recetas, 'Recetas a cocinar', SEG_META[segActivo].label, Boxes)}
        {renderItems(
          datos.insumos,
          'Para despacho — no requiere cocción',
          SEG_META[segActivo].label,
          Package
        )}
      </div>
    </section>
  );
};

export default VistaCocinaSegmentada;
